/**
 * The builder's header: where the admin is (build, then name and save), what is left to do before the
 * next stage, and the one action that moves on.
 */

import { useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { useCustomToolBuilder, type BuilderStage } from './builder-context';

const STAGES: { value: BuilderStage; label: string }[] = [
	{ value: 'build', label: 'Build' },
	{ value: 'details', label: 'Name and save' }
];

const StagePill = ({ value, label, index }: { value: BuilderStage; label: string; index: number }) => {
	const { stage, goTo, buildIssue } = useCustomToolBuilder();
	const active = stage === value;
	const done = value === 'build' && !buildIssue;
	const blocked = value === 'details' && !!buildIssue;
	return (
		<button
			type='button'
			aria-current={active ? 'step' : undefined}
			disabled={blocked}
			onClick={() => goTo(value)}
			className={cn(
				'flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs leading-4 whitespace-nowrap transition-colors',
				active ? 'border-indigo-100 bg-indigo-50/60 font-medium text-indigo-800' : 'border-gray-500 text-gray-900 hover:bg-gray-50',
				blocked && 'cursor-not-allowed opacity-50 hover:bg-transparent'
			)}
		>
			<span
				className={cn(
					'flex size-4 items-center justify-center rounded-full text-[10px] leading-none tabular-nums',
					active ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-900'
				)}
			>
				{done && !active ? <Check className='size-2.5' strokeWidth={3} /> : index + 1}
			</span>
			{label}
		</button>
	);
};

const Guidance = ({ text, error }: { text: string | null; error?: boolean }) =>
	text ? (
		<p role={error ? 'alert' : undefined} className={cn('truncate text-xs leading-4', error ? 'text-red-600' : 'text-gray-800')}>
			{text}
		</p>
	) : null;

/** What is left before naming, as a link to the step (or the inputs) to fix. */
const BuildGuidance = () => {
	const { buildIssue, focusOn } = useCustomToolBuilder();
	if (!buildIssue) return null;
	const { at } = buildIssue;
	if (!at) return <Guidance text={buildIssue.message} />;
	return (
		<button type='button' onClick={() => focusOn(at)} className='truncate text-xs leading-4 text-indigo-700 hover:underline'>
			{buildIssue.message}
		</button>
	);
};

const BuildActions = () => {
	const { buildIssue, goTo, toolId } = useCustomToolBuilder();
	return (
		<>
			<BuildGuidance />
			<PillButton variant='primary' className='shrink-0' disabled={!!buildIssue} onClick={() => goTo('details')}>
				{toolId ? 'Next: review and save' : 'Next: name it'}
			</PillButton>
		</>
	);
};

const DetailsActions = () => {
	const { draft, toolId, goTo, save } = useCustomToolBuilder();
	const [confirming, setConfirming] = useState(false);
	return (
		<>
			{save.error ? <Guidance text={save.error} error /> : <Guidance text={save.problem} />}
			<PillButton className='shrink-0' onClick={() => goTo('build')}>
				Back to steps
			</PillButton>
			{/* A saved tool is live, so saving over it asks first; nothing calls a new one yet. */}
			<PillButton
				variant='primary'
				className='shrink-0'
				disabled={!!save.problem || save.pending}
				onClick={toolId ? () => setConfirming(true) : save.run}
			>
				{save.pending ? 'Saving' : toolId ? 'Save' : 'Create'}
			</PillButton>
			<ConfirmDialog
				open={confirming}
				title='Save changes?'
				confirmLabel='Save'
				onCancel={() => setConfirming(false)}
				onConfirm={() => {
					setConfirming(false);
					save.run();
				}}
			>
				{`Protocols that call ${draft.slug} use these changes from their next call.`}
			</ConfirmDialog>
		</>
	);
};

export const StageHeader = ({ title, onBack }: { title: string; onBack: () => void }) => {
	const { stage } = useCustomToolBuilder();
	return (
		<header className='flex h-[60px] shrink-0 items-center gap-3 border-b border-gray-500 bg-white px-4'>
			<Button variant='ghost' size='icon' className='size-7 rounded-full' aria-label='Back to custom tools' onClick={onBack}>
				<ChevronLeft size={16} />
			</Button>
			<h1 className='min-w-0 max-w-64 shrink truncate text-base font-semibold'>{title}</h1>
			<nav aria-label='Builder stages' className='flex shrink-0 items-center gap-1.5'>
				{STAGES.map(({ value, label }, index) => (
					<div key={value} className='flex items-center gap-1.5'>
						{index > 0 && <ChevronRight className='size-3 text-gray-700' aria-hidden />}
						<StagePill value={value} label={label} index={index} />
					</div>
				))}
			</nav>
			<div className='ml-auto flex min-w-0 items-center gap-3'>{stage === 'build' ? <BuildActions /> : <DetailsActions />}</div>
		</header>
	);
};
